// Approved client testimonials (Linear PIX-137), ported from the testimonials
// reference (snippet-platform-band.html, snippet-pricing-pullquote.html,
// snippet-tool-brand-disambiguation.html). Each quote, name, title and company
// was approved in writing exactly as written here: any change needs the
// client's re-approval. Each quote is [before, bold, after]: the three parts
// joined are the approved text, and the middle part is the one phrase set in
// bold for skimmers (emphasis only, the words are unchanged). The disclosure
// line travels with every placement. Logos are the files each client supplied,
// unedited; `logoWidth` is the logo's width at the 26px card height (42 for
// square marks).
import type { CSSProperties } from 'react'
import Link from 'next/link'
import { ArrowUpRight } from '@/components/shell/primitives'
import './testimonials.css'

const TESTIMONIALS = {
  radiaite: {
    quote: [
      "“I ran Radar to clear the dust and get input on issues that weren't on my personal radar yet. It's a quick, full-feature audit with a strong focus on AI visibility. And ",
      'I like paying per audit run',
      ': no large package, just a few checks over the course of a planned improvement.”',
    ],
    name: 'Christopher',
    role: null,
    company: 'RADIAITE',
    url: 'https://radiaite.com',
    logo: '/testimonials/radiaite-logo-dark-no-tagline.svg',
    logoWidth: 88,
    mark: false,
  },
  sidequesthq: {
    quote: [
      '“I wanted to see how AI describes my three sites and how to improve those answers. Radar showed me exactly what AI models think about our brand. For example, ',
      'it caught ChatGPT confusing Side Quest HQ with a tabletop gaming community',
      '. That pushed me to clearly define what we do, what we sell, and who we serve. I updated our website to match, and now AI has a precise definition that sets us apart from other brands with the same name.”',
    ],
    name: 'Lane Belone',
    role: 'Founder',
    company: 'Side Quest HQ',
    url: 'https://sidequesthq.co',
    logo: '/testimonials/side-quest-hq-logo.png',
    logoWidth: 42,
    mark: true,
  },
  wreb: {
    quote: [
      '“I would definitely recommend Radar to another business. It gives useful insights, but just as importantly, there is clearly someone behind it who ',
      'takes the results and customer feedback seriously',
      ' and is willing to improve the product when something does not make sense.”',
    ],
    name: 'Peter Berwanger',
    role: 'Managing Director',
    company: 'WREB.DEsign GmbH',
    url: 'https://wreb.de',
    logo: '/testimonials/wreb-design-logo.svg',
    logoWidth: 158,
    mark: false,
  },
} as const

type Client = keyof typeof TESTIMONIALS

const DISCLOSURE =
  'Featured clients received complimentary audit credits for taking the time to give feedback.'

function Quote({ client }: { client: Client }) {
  const [before, bold, after] = TESTIMONIALS[client].quote
  return (
    <p>
      {before}
      <strong>{bold}</strong>
      {after}
    </p>
  )
}

function Who({ client }: { client: Client }) {
  const t = TESTIMONIALS[client]
  return (
    <>
      <span className='tm-name'>
        <b>{t.name}</b>
        <small>
          {t.role ? `${t.role}, ` : null}
          <a href={t.url} target='_blank' rel='noopener'>
            {t.company}
          </a>
        </small>
      </span>
      <a
        className={t.mark ? 'tm-logo tm-logo--mark' : 'tm-logo'}
        href={t.url}
        target='_blank'
        rel='noopener'
        aria-label={`${t.company} website (opens in a new tab)`}
      >
        <img
          src={t.logo}
          alt={t.company}
          width={t.logoWidth}
          height={t.mark ? 42 : 26}
          loading='lazy'
        />
      </a>
    </>
  )
}

function TestimonialHeader({ client }: { client: Client }) {
  return (
    <figcaption className='tm-header'>
      {/* A square mark sits beside the name; a wide wordmark (WREB) stacks
          above it, as in the /platform band. */}
      <div
        className={
          TESTIMONIALS[client].mark
            ? 'tm-profile tm-profile--mark'
            : 'tm-profile'
        }
      >
        <Who client={client} />
      </div>
      <span className='tm-glyph' aria-hidden='true'>
        {'“'}
      </span>
    </figcaption>
  )
}

/** /platform: the "Client feedback" band between Pricing and Signal to fix. */
export function TestimonialsBand() {
  const clients: Client[] = ['radiaite', 'sidequesthq', 'wreb']
  return (
    <section
      className='sheet sheet--pad'
      id='feedback'
      data-rail='Client feedback'
      style={
        {
          '--bg': 'var(--paper-2)',
          paddingBottom: 'var(--section)',
          scrollMarginTop: '80px',
        } as CSSProperties
      }
    >
      <div className='container'>
        <div className='sec-head sec-head--split' data-reveal=''>
          <div style={{ display: 'grid', gap: '22px' } as CSSProperties}>
            <span className='eyebrow'>{'In their words'}</span>
            <h2 className='h-display h2'>
              {"They ran the audit. Here's what they "}
              <span className='serif'>{'said.'}</span>
            </h2>
          </div>
          <p className='lede'>
            {'Quotes from Radar users, published with their written approval.'}
          </p>
        </div>
        <div className='tm-grid reveal-stagger' data-reveal=''>
          {clients.map(client => (
            <figure className='tm-card' key={client}>
              <TestimonialHeader client={client} />
              <blockquote className='tm-quote'>
                <Quote client={client} />
              </blockquote>
            </figure>
          ))}
        </div>
        <p className='tm-note tm-band-note'>{DISCLOSURE}</p>
      </div>
    </section>
  )
}

/** Single pull quote: /pricing (RADIAITE); the brand-disambiguation tool page and the homepage's 02 / The decision stage (Side Quest HQ). `moreHref` adds a link to the full client feedback band (homepage). */
export function TestimonialSolo({
  client,
  tag,
  onPaper = false,
  moreHref,
  featured = false,
}: {
  client: Client
  tag: string
  onPaper?: boolean
  moreHref?: string
  featured?: boolean
}) {
  if (featured) {
    return (
      <figure className='tm-solo tm-featured' data-reveal=''>
        <TestimonialHeader client={client} />
        <div className='tm-solo__lead'>
          <span className='tm-tag'>{'From a Radar user'}</span>
          <blockquote className='tm-quote'>
            <Quote client={client} />
          </blockquote>
        </div>
        <div className='tm-featured__footer'>
          <p className='tm-note'>{DISCLOSURE}</p>
          {moreHref && (
            <Link className='tm-more' href={moreHref}>
              {'Read all client feedback'}
              <ArrowUpRight />
            </Link>
          )}
        </div>
      </figure>
    )
  }

  return (
    <figure
      className={onPaper ? 'tm-solo tm--onpaper' : 'tm-solo'}
      data-reveal=''
    >
      <div className='tm-solo__lead'>
        <span className='tm-tag'>
          {`${tag} · `}
          <b>{'Radar user'}</b>
        </span>
        <blockquote className='tm-quote'>
          <Quote client={client} />
        </blockquote>
      </div>
      <figcaption className='tm-solo__side'>
        <div className='tm-who'>
          <Who client={client} />
        </div>
        <p className='tm-note'>{DISCLOSURE}</p>
        {moreHref && (
          <Link className='tm-more' href={moreHref}>
            {'Read all client feedback'}
            <ArrowUpRight />
          </Link>
        )}
      </figcaption>
    </figure>
  )
}
